import { Modal, Row, Col, Badge } from 'react-bootstrap';
import Button from 'components/base/Button';

// ✅ FIXED: Use DisplayAsset interface to match AssetTable
interface DisplayAsset {
  id: number;
  name: string;
  asset_tag: string;
  serial_number: string;
  brand: string;
  model: string;
  description: string;
  status: 'available' | 'assigned' | 'maintenance' | 'retired';
  condition: 'excellent' | 'good' | 'fair' | 'poor';
  purchase_date: string;
  purchase_cost: number;
  purchase_invoice_number: string;
  vendor: string;
  warranty_expiry_date: string;
  location: string;
  company_id: number;
  category_id: number;
  current_holder_id: number;
  created_at: string;
  updated_at: string;
  category_name?: string;
  current_holder_name?: string;
  company_name?: string;

  // Display fields
  deviceName: string;
  serialNumber: string;
  category: string;
  assignedTo?: string;
}

interface ViewAssetModalProps {
  show: boolean;
  handleClose: () => void;
  asset: DisplayAsset | null;
}

const ViewAssetModal = ({ show, handleClose, asset }: ViewAssetModalProps) => {
  if (!asset) return null;

  // Helper function to format status badge
  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      available: 'success',
      assigned: 'primary',
      maintenance: 'warning',
      retired: 'danger'
    };
    return (
      <Badge bg={statusColors[status] || 'secondary'} className="fs-9">
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </Badge>
    );
  };

  // Helper function to format condition badge
  const getConditionBadge = (condition: string) => {
    const conditionColors: Record<string, string> = {
      excellent: 'success',
      good: 'info',
      fair: 'warning',
      poor: 'danger'
    };
    return (
      <Badge bg={conditionColors[condition] || 'secondary'} className="fs-9">
        {condition?.charAt(0).toUpperCase() + condition?.slice(1)}
      </Badge>
    );
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      keyboard={true}
      style={{ zIndex: 1050 }}
    >
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fs-6">
          <i className="fas fa-eye me-2"></i>
          Asset Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <div className="container-fluid">
          {/* Basic Information */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="text-primary mb-3 border-bottom pb-2">
                Basic Information
              </h6>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Device Name</label>
              <div className="fs-6">{asset.deviceName || '-'}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Category</label>
              <div className="fs-6">{asset.category || '-'}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Serial Number</label>
              <div className="fs-6">{asset.serial_number || '-'}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Asset Tag</label>
              <div className="fs-6">{asset.asset_tag || 'N/A'}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Status</label>
              <div className="fs-6">{getStatusBadge(asset.status)}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Condition</label>
              <div className="fs-6">{getConditionBadge(asset.condition)}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Brand</label>
              <div className="fs-6">{asset.brand || 'N/A'}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Model</label>
              <div className="fs-6">{asset.model || 'N/A'}</div>
            </div>
            <div className="col-md-12 mb-3">
              <label className="form-label text-700 mb-1">Description</label>
              <div className="fs-6" style={{ whiteSpace: 'pre-wrap' }}>
                {asset.description || 'No description provided'}
              </div>
            </div>
          </div>

          {/* Assignment & Location */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="text-primary mb-3 border-bottom pb-2">
                Assignment & Location
              </h6>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Assigned To</label>
              <div className="fs-6">{asset.assignedTo || 'Unassigned'}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Location</label>
              <div className="fs-6">{asset.location || 'N/A'}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Company</label>
              <div className="fs-6">{asset.company_name || 'N/A'}</div>
            </div>
          </div>

          {/* Purchase Information */}
          <div className="row mb-4">
            <div className="col-12">
              <h6 className="text-primary mb-3 border-bottom pb-2">
                Purchase Information
              </h6>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Purchase Date</label>
              <div className="fs-6">{formatDate(asset.purchase_date)}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Vendor</label>
              <div className="fs-6">{asset.vendor || 'N/A'}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">
                Warranty Expiry Date
              </label>
              <div className="fs-6">
                {formatDate(asset.warranty_expiry_date)}
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">
                Purchase Invoice Number
              </label>
              <div className="fs-6">
                {asset.purchase_invoice_number || 'N/A'}
              </div>
            </div>
          </div>

          {/* System Information */}
          <div className="row">
            <div className="col-12">
              <h6 className="text-primary mb-3 border-bottom pb-2">
                System Information
              </h6>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Created Date</label>
              <div className="fs-6">{formatDate(asset.created_at)}</div>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-700 mb-1">Last Updated</label>
              <div className="fs-6">{formatDate(asset.updated_at)}</div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0">
        <Button
          variant="phoenix-secondary"
          onClick={handleClose}
          className="px-4"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewAssetModal;
